import React, { FC, useEffect, useState } from 'react';
import { DropzoneAreaBase, FileObject } from 'material-ui-dropzone';
import { MdDescription } from 'react-icons/md';
import { Box, Button, Grid, GridItem, Link } from '@chakra-ui/react';

import { useAutocomplete, useGuide } from '@app/hooks';

import { AgreementFormInstance } from './types';
import { useEntityState } from '../../hooks/use-entity-state';
import AutocompleteField from '../common/autocomplete-field';
import SelectField from '../common/select-field';
import TextField from '../common/text-field';
import DateField from '../common/date-field';
import TransferList from '../common/transfer-list';
import { useEquipmentList } from './hooks/useEquipmentList';

interface AgreementFormProps {
  instance: Partial<AgreementFormInstance>;
  submitText?: string;
  onSubmit: (instance: Partial<AgreementFormInstance>) => void;
  onCancel: () => void;
  loading: boolean;
}

const AgreementForm: FC<AgreementFormProps> = (props) => {
  const { instance, submitText, onSubmit, onCancel, loading } = props;
  const { equipments, fetchEquipments } = useEquipmentList();
  const [files, setFiles] = useState<FileObject[]>([]);
  const [agreement, onChange, setInstance] =
    useEntityState<AgreementFormInstance>(instance);
  const getDocumentTypes = useGuide('document_type');


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setInstance(instance), [instance]);

  useEffect(() => {
    fetchEquipments({ distributor_id: agreement.distributor?.id?.toString(), outlet_id: agreement.outlet?.id?.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agreement.distributor, agreement.outlet]);

  const onAdd = (files: FileObject[]) => {
    if (!files[0].data) {
      return;
    }

    setFiles(files);
  };

  const getAutocomplete = useAutocomplete();

  return (
    <>
      <Grid gap={5} templateColumns={{ base: '1fr', md: '1fr 1fr' }} >
        <GridItem >
          <AutocompleteField
            id="outlet_id"
            name="outlets"
            label="Торговая точка/склад"
            value={agreement.outlet?.name || ''}
            onFetchOptions={getAutocomplete}
            onChange={onChange('outlet')}
          />
        </GridItem>

        <GridItem>
          <AutocompleteField
            id="distributor_id"
            name="distributor"
            label="Дистрибьютор"
            value={agreement.distributor?.name || ''}
            onFetchOptions={getAutocomplete}
            onChange={onChange('distributor')}
          ></AutocompleteField>
        </GridItem>
        <GridItem>
          <SelectField
            id="agreement_type_id"
            name="document_type"
            label="Тип"
            value={agreement.type || undefined}
            options={agreement.type ? [agreement.type] : []}
            onFetchOptions={getDocumentTypes}
            onChange={onChange('type')}
          ></SelectField>
        </GridItem>

        <GridItem>
          <TextField
            label="Номер"
            id="number"
            value={agreement.number}
            onChange={onChange('number')}
          />
        </GridItem>

        <GridItem colSpan={{ base: 1, md: 2 }}>
          <TextField
            label="Наименование"
            id="name"
            value={agreement.name}
            onChange={onChange('name')}
          />
        </GridItem>

        <GridItem>
          <DateField
            id="production_date"
            label="Дата начала"
            value={agreement.date_open || undefined}
            onChange={onChange('date_open')}
          />
        </GridItem>
        <GridItem>
          <DateField
            id="delivery_date"
            label="Дата окончания"
            value={agreement.date_close || undefined}
            onChange={onChange('date_close')}
          />
        </GridItem>

        <GridItem mt={5} colSpan={{ base: 1, md: 2 }}>
          <Box display="flex" justifyContent="center">
            {/* <PdfView open={true} url={agreement.image} setOpen={() => {}} title={''} /> */}
            <Link target="_blank" rel="noreferrer" href={agreement.image}>
              <Box display="flex" alignItems="center" fontSize="large">
                <MdDescription />
                Скачать файл соглашения
              </Box>
            </Link>
          </Box>
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <DropzoneAreaBase
            fileObjects={files}
            dropzoneText="Переместите сюда файл с данными или нажмите для вызова диалога"
            onAdd={onAdd}
          />
        </GridItem>
        {new Set(['2', '4']).has(agreement.type?.id?.toString() || '') && (
          <GridItem colSpan={{ base: 1, md: 2 }}>
            <TransferList
              items={equipments.filter(
                (item) =>
                  !new Set(agreement.equips?.map(({ id }) => id)).has(item.id)
              )}
              selected={agreement.equips?.map((item) => ({
                id: item.id,
                name: item.inventory_num,
              }))}
              leftSideTitle="Доступные ХО:"
              rightSideTitle="Выбранные ХО:"
              onChange={(left, right) => onChange('equips')(right)}
            />
          </GridItem>
        )}
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <TextField
            id="comment"
            label="Комментарий"
            multiline
            rows={4}
            value={agreement.comment}
            onChange={onChange('comment')}
          />
        </GridItem>
        <GridItem colSpan={{ base: 1, md: 2 }}>
          <Box display="flex" justifyContent="space-between">
            <Button
              isLoading={loading}
              colorScheme='brand'
              size="md"
              onClick={() => onSubmit({ ...agreement, files })}
            >
              {submitText || 'Создать'}
            </Button>
            <Button
              variant="ghost"
              size="md"
              onClick={onCancel}
            >
              Отмена
            </Button>
          </Box>
        </GridItem>
      </Grid>
    </>
  );
};

export default AgreementForm;
